import type { Agg, DataFrame, Encoding, Row, Widget } from "../types";

/**
 * Categorical palette — fixed order, never cycled.
 *
 * Validated with the data-viz palette checker against our card surface
 * (#1e2029, dark): lightness band PASS, chroma floor PASS, adjacent CVD ΔE 8.4
 * (protan) PASS, normal-vision ΔE 19.3 PASS, contrast >= 3:1 PASS.
 *
 * It replaces the previous hand-picked set, which FAILED: its green (#5fb878)
 * and gold (#c0af73) sat ΔE 0.8 apart under protanopia and ΔE 11.6 for normal
 * vision — two series that were, in practice, the same colour. The gold also
 * read as grey (chroma 0.081, below the 0.1 floor).
 *
 * Slot 1 is a violet close to the brand indigo, so the house colour still leads.
 * Do NOT reorder without re-running the validator: the checks are on ADJACENT
 * pairs, so order is part of what passes.
 */
export const PALETTE = [
  "#9085e9", // violet — brand-adjacent, leads
  "#e66767", // red
  "#3987e5", // blue
  "#d95926", // orange
  "#199e70", // aqua
  "#c98500", // yellow
  "#d55181", // magenta
  "#008300", // green
];

/** Distinct values of a column, preserving first-seen order. */
export function distinct(rows: Row[], col: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of rows) {
    const v = String(r[col] ?? "");
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

export function aggregate(values: number[], agg: Agg): number {
  if (agg === "count") return values.length;
  if (values.length === 0) return 0;
  const sum = values.reduce((s, v) => s + v, 0);
  switch (agg) {
    case "sum":
      return sum;
    case "avg":
      return Math.round((sum / values.length) * 100) / 100;
    case "min":
      return Math.min(...values);
    case "max":
      return Math.max(...values);
    default:
      return sum;
  }
}

interface SeriesData {
  categories: string[];
  series: { name: string; data: number[] }[];
}

/** Group/aggregate rows into categories x series according to the encoding. */
export function buildSeries(ds: DataFrame, enc: Encoding): SeriesData {
  const xcol = enc.x;
  const categories = xcol ? distinct(ds.rows, xcol) : ["Всего"];
  const measure = enc.y[0];

  const valuesFor = (predicate: (r: Row) => boolean, col: string) =>
    ds.rows.filter(predicate).map((r) => Number(r[col]) || 0);

  let series: { name: string; data: number[] }[] = [];

  if (enc.series && measure) {
    // one series per distinct legend value
    const keys = distinct(ds.rows, enc.series);
    series = keys.map((k) => ({
      name: k,
      data: categories.map((cat) =>
        aggregate(
          valuesFor(
            (r) =>
              (!xcol || String(r[xcol]) === cat) &&
              String(r[enc.series!]) === k,
            measure,
          ),
          enc.agg,
        ),
      ),
    }));
  } else {
    // one series per chosen measure column
    series = enc.y.map((m) => ({
      name: m,
      data: categories.map((cat) =>
        aggregate(valuesFor((r) => !xcol || String(r[xcol]) === cat, m), enc.agg),
      ),
    }));
  }

  return { categories, series };
}

const axisChrome = {
  textStyle: { color: "#a0a3b1" },
  axisLine: { lineStyle: { color: "rgba(255,255,255,0.12)" } },
  splitLine: { lineStyle: { color: "rgba(255,255,255,0.06)" } },
};

/** Build an ECharts option for line / bar / pie widgets. */
export function buildOption(widget: Widget, ds: DataFrame): Record<string, unknown> {
  const enc = widget.encoding;

  if (widget.type === "pie") {
    const cat = enc.x;
    const measure = enc.y[0];
    const cats = cat ? distinct(ds.rows, cat) : [];
    const data = cats.map((c) => ({
      name: c,
      value: aggregate(
        ds.rows
          .filter((r) => String(r[cat!]) === c)
          .map((r) => Number(r[measure]) || 0),
        enc.agg,
      ),
    }));
    return {
      color: PALETTE,
      tooltip: { trigger: "item" },
      legend: { textStyle: { color: "#a0a3b1" }, bottom: 0 },
      series: [
        {
          type: "pie",
          radius: ["45%", "70%"],
          center: ["50%", "45%"],
          data,
          label: { color: "#ececf1" },
        },
      ],
    };
  }

  const { categories, series } = buildSeries(ds, enc);
  return {
    color: PALETTE,
    tooltip: { trigger: "axis" },
    legend: { textStyle: { color: "#a0a3b1" }, top: 0 },
    grid: { left: 48, right: 16, top: 32, bottom: 28 },
    xAxis: {
      type: "category",
      data: categories,
      axisLabel: { hideOverlap: true },
      ...axisChrome,
    },
    yAxis: { type: "value", ...axisChrome },
    series: series.map((s) => ({
      name: prettyLabel(s.name),
      type: widget.type, // "line" | "bar"
      data: s.data,
      smooth: widget.type === "line",
      areaStyle: widget.type === "line" ? { opacity: 0.12 } : undefined,
      barMaxWidth: 28,
    })),
  };
}

/** Single aggregated value for the stat card. */
export function buildStat(widget: Widget, ds: DataFrame): number {
  const col = widget.encoding.metric ?? widget.encoding.y[0];
  if (!col) return 0;
  return aggregate(ds.rows.map((r) => Number(r[col]) || 0), widget.encoding.agg);
}

/** Group thousands with a space, dot as decimal separator: 40 355 424 / 10.79 */
export function formatNumber(v: number): string {
  const neg = v < 0;
  const abs = Math.abs(v);
  const s = Number.isInteger(abs) ? abs.toString() : abs.toFixed(2);
  const [int, dec] = s.split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return (neg ? "-" : "") + grouped + (dec ? "." + dec : "");
}

/**
 * Compact form for KPI cards: `63 672 837.05` -> `63.67M`.
 *
 * Cards are fixed-size tiles, so a full-precision financial figure wraps onto
 * three lines and clips — which is exactly what happened on the Taxes page at a
 * narrower window width. The source Power BI report has the same constraint and
 * solves it the same way ("88,10 млн"), so this is both the fix and the more
 * faithful rendering.
 *
 * Below a million the full grouped number fits, so it is kept — a card reading
 * `847 293` is more useful than `0.85M`.
 */
export function formatCompact(v: number): string {
  const abs = Math.abs(v);
  if (abs < 1_000_000) return formatNumber(v);
  const sign = v < 0 ? "-" : "";
  const [scale, suffix] =
    abs >= 1_000_000_000 ? [1_000_000_000, "B"] : [1_000_000, "M"];
  return `${sign}${(abs / scale).toFixed(2)}${suffix}`;
}

/** "product_group" -> "Product Group", "revenue" -> "Revenue". */
export function prettyLabel(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
